import {createPopper} from '@popperjs/core';
import {action, observable} from "mobx";
import {ReactNode} from "react";

import {ITooltipOptions, ITooltipService, TooltipObj} from "./tooltip.service.interface";
import {Check} from "@sparkware/app-utils";
import uuid from "uuid";
import {ServiceBase} from "../ServiceBase";

import {
    flipTooltipModifier,
    offsetTooltipModifier,
    preventOverflowTooltipModifier,
} from "./tooltip.modifiers";
import {tooltipDefaultOptions} from "./tooltip.default";
import {namedSetTimeout} from "../../../common/utils/time-functions";

export class TooltipService extends ServiceBase implements ITooltipService {
    _popperInstance = null;
    @observable.ref currentToolTip: TooltipObj = null;

    @observable.ref _tooltipContainer: HTMLElement;
    _tooltipsQueue: TooltipObj[] = [];

    constructor(params) {
        super(params);
        this._subscribe();
    }

    _subscribe() {
        // If we got queue of tooltip, once one is closed, open the next one
        this.reaction(() => ({
            currentToolTip: this.currentToolTip
        }), ({currentToolTip}) => {
            if (!currentToolTip) {
                this._openNextTooltip();
            }
        });

        //On screen resize, destroy and recreate current tooltip
        this.services.window.subscribeTo.resize(() => {
            if (this.currentToolTip) {
                this.currentToolTip.show();
            }
        });

        // Close the tooltip on click outside of it (If enabled by the 'closeOnClickOutside' options
        this.services.dom.subscribeTo.click(this._handleClickOutsideTooltip);
    }

    _handleClickOutsideTooltip = (event) => {
        if (this.currentToolTip && !this._tooltipContainer.contains(event.target)) {
            const {closeOnClickOutside = true} = this.currentToolTip.options || {}
            closeOnClickOutside && this.currentToolTip.close();
        }
    }

    @action.bound
    _showTooltip(tooltipObj: TooltipObj, modifiers) {

        if (!this._tooltipContainer)
            return null;

        const scrollContainer = this.services.window.scrollContainer;

        const {options} = tooltipObj;
        const {attach, showOverlay} = options;

        let attachToElement = null;

        if (!attach) {
            attachToElement = this.services.window.scrollContainer; //To be changed
        } else if (Check.isString(attach)) { // query selector
            attachToElement = document.querySelector(attach);
        } else if (attach instanceof HTMLElement) {
            attachToElement = attach;
        }

        if (!attachToElement)
            throw Error("Couldn't attach tooltip to query selector");

        if (showOverlay) {
            this.services.overlay.openOverlay();
        }

        this._popperInstance && this._popperInstance.destroy();

        this._popperInstance = createPopper(attachToElement, this._tooltipContainer, {
            placement: options.placement || 'auto',
            strategy: "fixed",
            modifiers: [
                preventOverflowTooltipModifier(scrollContainer),
                flipTooltipModifier(scrollContainer),
                offsetTooltipModifier(scrollContainer),
                ...modifiers.map((modifier) => modifier.call(this, scrollContainer))
            ]
        });

        if(Check.isNumber(options.timeout)){
            tooltipObj.closeTimeoutId = namedSetTimeout('TOOLTIP_CLOSE_TIMER', tooltipObj.close, options.timeout);
        }

        this.currentToolTip = tooltipObj;

        return tooltipObj;
    }

    @action.bound
    closeCurrentTooltip(){
        this.currentToolTip && this.currentToolTip.close();
    }

    @action.bound
    _closeToolTip(tooltipObj: TooltipObj) {
        if (this._popperInstance) {
            this._popperInstance.destroy();
        }

        if(tooltipObj.closeTimeoutId){
            clearTimeout(this.currentToolTip.closeTimeoutId);
            tooltipObj.closeTimeoutId = null;
        }

        if (tooltipObj.options?.onTooltipClosed) {
            tooltipObj.options.onTooltipClosed();
        }

        const currentTooltipIndex = this._tooltipsQueue.findIndex(({id}) => id === tooltipObj.id);

        //Remove from Queue
        currentTooltipIndex >= 0 && this._tooltipsQueue.splice(currentTooltipIndex, 1);

        if (this.currentToolTip?.id === tooltipObj.id) { //Current tooltip is the one we want to close (Normal behavior)
            this.currentToolTip.options.showOverlay && this.services.overlay.closeOverlay(); // Close overlay is was open on the current tooltip
            this.currentToolTip = null;
        }
    }

    _openNextTooltip() {
        if (this.currentToolTip) {
            this.currentToolTip.close();
        }

        if (this._tooltipsQueue.length > 0) {
            this._tooltipsQueue.pop().show();
        }

        return this.currentToolTip;
    }

    @action.bound
    setTooltipContainer(element: HTMLElement) {
        this._tooltipContainer = element;
    }

    @action.bound
    createTooltip(content: string | ReactNode, options: ITooltipOptions = {}, modifiers = []) {
        const tooltipObj: TooltipObj = {
            id: uuid(),
            show: () => this._showTooltip(tooltipObj, modifiers),
            close: () => this._closeToolTip(tooltipObj),
            next: () => this._openNextTooltip(),
            options: {...tooltipDefaultOptions, ...options},
            content
        }

        if (!this.currentToolTip) {
            tooltipObj.show();
        } else {
            this._tooltipsQueue.push(tooltipObj)
        }
    }
}